import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { ReactComponent as DownloadIcon } from '../../assets/icons/arrow-circle-down-solid.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/expand-arrows-alt-solid.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash-alt-solid.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/times-solid.svg';
import { Preview3DView } from './Preview3DView';

const useCyclingMessage = (isActive: boolean, messages: string[], interval: number = 3000) => {
	const [messageIndex, setMessageIndex] = useState(0);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (!isActive) {
			setMessageIndex(0);
			return;
		}

		intervalRef.current = setInterval(() => {
			setMessageIndex((prev) => (prev + 1) % messages.length);
		}, interval);

		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [isActive, messages.length, interval]);

	return messages[messageIndex] || '';
};

const ProgressBar: React.FC<{ duration?: number }> = ({ duration = 60000 }) => {
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		const steps = 10;
		const stepInterval = duration / steps;
		let currentStep = 0;

		const timer = setInterval(() => {
			currentStep++;
			setProgress((currentStep / steps) * 95);
			if (currentStep >= steps) clearInterval(timer);
		}, stepInterval);

		return () => clearInterval(timer);
	}, [duration]);

	return (
		<ProgressBarContainer>
			<ProgressBarFill $progress={progress} />
		</ProgressBarContainer>
	);
};

// Simple ARIA-compliant button component
const ARIAButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

interface ImagesContainerProps {
	hasGenerated: boolean;
	variants?: Array<{ id: number; url: string }>;
	onVariantClick?: (variantId: number) => void;
	selectedVariantId?: number;
	isActive3D?: boolean;
	isLoadingGenerate?: boolean;
	isInert?: boolean;
	onUseAsInput?: (imageUrl: string) => void;
	inputImage?: string | null;
	onClearInputImage?: () => void;
	onDeleteVariant?: (variantId: number) => void;
}

export const ImagesContainer: React.FC<ImagesContainerProps> = ({
	hasGenerated,
	variants = [],
	onVariantClick,
	selectedVariantId,
	isActive3D = false,
	isLoadingGenerate = false,
	isInert = false,
	onUseAsInput,
	inputImage,
	onClearInputImage,
	onDeleteVariant,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
	const GENERATING_PHRASES = ['Generating...', 'Creating your image...', 'Almost there...'];
	const currentLoadingMessage = useCyclingMessage(isLoadingGenerate, GENERATING_PHRASES, 3000);

	// Function to handle image download
	const handleDownload = async (imageUrl: string, variantId: number, event: React.MouseEvent) => {
		event.stopPropagation();

		try {
			const response = await fetch(imageUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `ai-generated-variant-${variantId}.png`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading image:', error);
		}
	};


	// Function to handle fullscreen
	const handleFullscreen = (imageUrl: string, event: React.MouseEvent) => {
		event.stopPropagation();
		setFullscreenImage(imageUrl);
	};

	// Function to handle delete
	const handleDelete = (variantId: number, event: React.MouseEvent) => {
		event.stopPropagation();

		if (onDeleteVariant) {
			onDeleteVariant(variantId);
		}
	};

	return (
		<CanvasContainer {...(isInert ? { inert: 'inert' } : {})}>
			{isLoadingGenerate ? (
				<InitialState>
					<LoadingBox>
						<LoadingText>{currentLoadingMessage}</LoadingText>
						<ProgressBar duration={60000} />
					</LoadingBox>
				</InitialState>
			) : hasGenerated ? (
				// Show grid with generated images after generation
				<VariantsState>
					<VariantsGrid $imageCount={variants.length}>
						{variants.map((variant, index) => (
							<VariantCard
								key={variant.id}
								onClick={() => onVariantClick?.(variant.id)}
								delay={index * 0.1}
							>
								<img src={variant.url} alt={T._('AI Generated product preview')} />

								{/* Top-right actions: Fullscreen and Delete */}
								<TopRightActions>
									<ActionButton
										onClick={(e) => handleFullscreen(variant.url, e)}
										aria-label={T._('View fullscreen')}
										title={T._('View fullscreen')}
									>
										<ExpandIcon aria-hidden='true' />
									</ActionButton>
									{onDeleteVariant && (
										<ActionButton
											onClick={(e) => handleDelete(variant.id, e)}
											aria-label={T._('Delete')}
											title={T._('Delete')}
										>
											<TrashIcon aria-hidden='true' />
										</ActionButton>
									)}
								</TopRightActions>

								{/* Download overlay (center) */}
								<DownloadOverlay>
									<ARIAButton
										onClick={(e) => handleDownload(variant.url, variant.id, e)}
										aria-label={T._('Download generated image')}
									>
										<DownloadIcon aria-hidden='true' />
									</ARIAButton>
								</DownloadOverlay>

								{/* Use as Input button (bottom) - temporarily disabled
								<UseAsInputOverlay>
									<UseAsInputButton
										onClick={(e) => handleUseAsInput(variant.url, e)}
										aria-label={T._('Use as Input')}
										title={T._('Use as Input')}
									>
										<ArrowRightIcon aria-hidden='true' />
										<span>{T._('Use as Input')}</span>
									</UseAsInputButton>
								</UseAsInputOverlay>
								*/}
							</VariantCard>
						))}
					</VariantsGrid>
				</VariantsState>
			) : inputImage ? (
				// Show input image preview when set
				<InitialState>
					<InputImageContainer>
						<InputImageTitle>{T._('Custom Input Image')}</InputImageTitle>
						<InputImageWrapper>
							<img src={inputImage} alt={T._('Input image')} />
							{onClearInputImage && (
								<ClearInputButton onClick={onClearInputImage} title={T._('Clear and return to 3D view')}>
									×
								</ClearInputButton>
							)}
						</InputImageWrapper>
						<HelpText>{T._('This image will be used as input for generation')}</HelpText>
					</InputImageContainer>
				</InitialState>
			) : (
				// Show 3D view by default
				<InitialState>
					<Preview3DViewWrapper>
						<Preview3DView />
					</Preview3DViewWrapper>
					<HelpText>{T._('Rotate the 3D view to your desired angle, then click Generate')}</HelpText>
				</InitialState>
			)}

			{/* Fullscreen Modal */}
			{fullscreenImage && (
				<FullscreenModal onClick={() => setFullscreenImage(null)}>
					<FullscreenImageWrapper>
						<img src={fullscreenImage} alt={T._('AI Generated product preview')} />
						<CloseButton
							onClick={() => setFullscreenImage(null)}
							aria-label={T._('Close')}
							title={T._('Close')}
						>
							<CloseIcon aria-hidden='true' />
						</CloseButton>
					</FullscreenImageWrapper>
				</FullscreenModal>
			)}
		</CanvasContainer>
	);
};

// Styled Components

const CanvasContainer = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem;
	overflow: hidden;

	@media (min-width: 640px) {
		padding: 1rem;
	}

	@media (min-width: 1536px) {
		padding: 2rem;
	}
`;

const InitialState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	animation: fadeIn 0.4s ease-out forwards;
	width: 100%;
	height: 100%;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const Preview3DViewWrapper = styled.div`
	width: 100%;
	height: 400px;
	max-width: 400px;
	border-radius: 1rem;
	overflow: hidden;

	@media (max-width: 640px) {
		height: 300px;
	}
`;

const HelpText = styled.p`
	color: #9ca3af;
	font-size: 14px;
	margin-top: 1rem;
	text-align: center;
	max-width: 20rem;
	padding: 0 1rem;

	@media (min-width: 640px) {
		margin-top: 1.5rem;
	}
`;

const VariantsState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: slideIn 0.4s ease-out forwards;
	width: 100%;
	height: 100%;
	overflow-y: auto;

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateX(-20px);
		}
		to {
			opacity: 1;
			transform: translateX(0);
		}
	}
`;

const VariantsGrid = styled.div<{ $imageCount: number }>`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem;
	max-width: 800px;
	width: 100%;
	padding: 0 0.5rem;

	@media (min-width: 768px) {
		grid-template-columns: ${(props) =>
			props.$imageCount < 3 ? `repeat(${props.$imageCount}, 1fr)` : 'repeat(3, 1fr)'};
		gap: 1.5rem;
	}

	@media (min-width: 1024px) {
		grid-template-columns: ${(props) =>
			props.$imageCount < 3 ? `repeat(${props.$imageCount}, 1fr)` : 'repeat(4, 1fr)'};
	}
`;

const VariantCard = styled.div<{ delay: number }>`
	width: 100%;
	max-width: 400px;
	max-height: 400px;
	min-width: 0;
	min-height: 0;
	aspect-ratio: 1;
	border-radius: 0.75rem;
	overflow: hidden;
	background: linear-gradient(to bottom right, #faf5ff, #f3e8ff);
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 2px 4px -1px rgba(0, 0, 0, 0.06);
	border: 1px solid #e5e7eb;
	cursor: pointer;
	transition: all 0.2s;
	flex-shrink: 0;
	opacity: 0;
	animation: fadeInUp 0.5s ease-out forwards;
	animation-delay: ${(props) => props.delay}s;
	position: relative;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&:hover {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
		transform: scale(1.02);
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
`;

const DownloadOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.4);
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 1;
	pointer-events: none;

	${VariantCard}:hover & {
		opacity: 1;
		pointer-events: auto;
	}

	button {
		width: 100%;
		height: 100%;
		align-items: center;
		justify-content: center;
	}

	svg {
		width: 3rem;
		height: 3rem;
		fill: white;
		filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
		transition: transform 0.2s ease;
	}

	&:hover svg {
		transform: scale(1.1);
	}
`;



const LoadingBox = styled.div`
	width: 100%;
	max-width: 400px;
	height: 100%;
	max-height: 400px;
	border-radius: 0.75rem;
	overflow: hidden;
	background: linear-gradient(to bottom right, #faf5ff, #f3e8ff);
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 2px 4px -1px rgba(0, 0, 0, 0.06);
	border: 1px solid #e5e7eb;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	padding: 2rem;
	animation: fadeIn 0.4s ease-out forwards;
`;

const LoadingText = styled.p`
	font-size: 1rem;
	font-weight: 500;
	color: #6b7280;
	margin: 0;
	text-align: center;
`;

const ProgressBarContainer = styled.div`
	width: 100%;
	height: 4px;
	background-color: #dbd3f8;
	border-radius: 9999px;
	overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $progress: number }>`
	height: 100%;
	width: ${(props) => props.$progress}%;
	background: linear-gradient(90deg, #a855f7, #ec4899);
	border-radius: 9999px;
	transition: width 0.5s ease;
`;

const InputImageContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	animation: fadeIn 0.4s ease-out forwards;
	width: 100%;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
`;

const InputImageTitle = styled.h3`
	font-size: 16px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 1rem;
	text-align: center;
`;

const InputImageWrapper = styled.div`
	position: relative;
	width: 100%;
	max-width: 400px;
	height: auto;
	max-height: 400px;
	aspect-ratio: 1;
	border-radius: 1rem;
	overflow: hidden;
	border: 2px solid #8b5cf6;
	box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.2);

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const ClearInputButton = styled.button`
	position: absolute;
	top: 8px;
	right: 8px;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.6);
	color: white;
	border: none;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.8);
		transform: scale(1.1);
	}
`;

const TopRightActions = styled.div`
	position: absolute;
	top: 0.5rem;
	right: 0.5rem;
	display: flex;
	gap: 0.5rem;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 3;
	pointer-events: none;

	${VariantCard}:hover & {
		opacity: 1;
		pointer-events: auto;
	}
`;

const ActionButton = styled.button`
	background: rgba(0, 0, 0, 0.6);
	border: none;
	border-radius: 0.375rem;
	padding: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;

	svg {
		width: 1rem;
		height: 1rem;
		fill: white;
	}

	&:hover {
		background: rgba(0, 0, 0, 0.8);
		transform: scale(1.1);
	}
`;

const FullscreenModal = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.95);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 2rem;
	animation: fadeIn 0.2s ease-out;

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`;

const FullscreenImageWrapper = styled.div`
	position: relative;
	max-width: 90vw;
	max-height: 90vh;
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		max-width: 100%;
		max-height: 90vh;
		object-fit: contain;
		border-radius: 0.5rem;
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: -3rem;
	right: 0;
	background: rgba(255, 255, 255, 0.1);
	border: none;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;

	svg {
		width: 1.25rem;
		height: 1.25rem;
		fill: white;
	}

	&:hover {
		background: rgba(255, 255, 255, 0.2);
		transform: scale(1.1);
	}
`;
